import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Wallet, 
  Shield, 
  Lock, 
  Unlock,
  RefreshCw,
  Send,
  Plus,
  AlertCircle,
  CheckCircle,
  Loader2,
  ArrowRight,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MiniBankDashboard({ onTransferClick }) {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloadDialog, setReloadDialog] = useState({ open: false, clubId: null, clubName: '' });
  const [reloadAmount, setReloadAmount] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadBalance();
  }, []);

  const loadBalance = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/minibank/balance`, {
        withCredentials: true
      });
      setBalance(response.data);
    } catch (error) {
      console.error('Load balance error:', error);
      toast.error('Failed to load Mini Bank balance');
    } finally {
      setLoading(false);
    }
  };

  const handleReloadProtection = async () => {
    if (!reloadAmount || parseFloat(reloadAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const amount = parseFloat(reloadAmount);
    if (amount > (balance?.available_balance || 0)) {
      toast.error('Insufficient available balance');
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/minibank/reload-protection`,
        {
          club_id: reloadDialog.clubId,
          amount: amount
        },
        { withCredentials: true }
      );

      toast.success(response.data.message);
      setReloadDialog({ open: false, clubId: null, clubName: '' });
      setReloadAmount('');
      loadBalance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reload protection');
    } finally {
      setProcessing(false);
    }
  };

  const openReloadDialog = (clubId, clubName) => {
    setReloadDialog({ open: true, clubId, clubName });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'critical': return 'text-red-600 bg-red-50';
      case 'warning': return 'text-amber-600 bg-amber-50';
      case 'healthy': return 'text-green-600 bg-green-50';
      default: return 'text-stone-600 bg-stone-50';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'critical': return <AlertCircle className="w-4 h-4" />;
      case 'warning': return <AlertCircle className="w-4 h-4" />;
      case 'healthy': return <CheckCircle className="w-4 h-4" />;
      default: return <Shield className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <Card className="border-stone-200">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-stone-200 overflow-hidden" data-testid="minibank-dashboard">
        <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center text-xl">
                <Wallet className="w-5 h-5 mr-2" />
                Mini Bank
              </CardTitle>
              <CardDescription className="text-indigo-100 mt-1">
                Your internal savings account
              </CardDescription>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={loadBalance}
              className="bg-white/20 hover:bg-white/30 text-white"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {/* Balance Overview */}
          <div className="p-6 bg-gradient-to-b from-indigo-50 to-white">
            <div className="grid grid-cols-3 gap-4">
              {/* Total Balance */}
              <div className="text-center">
                <div className="text-sm text-stone-500 mb-1">Total Savings</div>
                <div className="text-2xl font-bold text-stone-900">
                  ${balance?.total_savings?.toFixed(2) || '0.00'}
                </div>
              </div>

              {/* Locked */}
              <div className="text-center border-x border-stone-200">
                <div className="text-sm text-stone-500 mb-1 flex items-center justify-center">
                  <Lock className="w-3 h-3 mr-1" />
                  Protected
                </div>
                <div className="text-2xl font-bold text-amber-600">
                  ${balance?.total_protection_locked?.toFixed(2) || '0.00'}
                </div>
              </div>

              {/* Available */}
              <div className="text-center">
                <div className="text-sm text-stone-500 mb-1 flex items-center justify-center">
                  <Unlock className="w-3 h-3 mr-1" />
                  Available
                </div>
                <div className="text-2xl font-bold text-green-600">
                  ${balance?.available_balance?.toFixed(2) || '0.00'}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex gap-3 mt-6">
              <Button
                className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                onClick={onTransferClick}
                data-testid="send-money-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Send Money
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => window.location.href = '/clubs'}
                data-testid="contribute-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Contribute
              </Button>
            </div>
          </div>

          {/* Protection Status */}
          {balance?.protection_status?.length > 0 && (
            <div className="border-t border-stone-200">
              <div className="px-6 py-3 bg-stone-50 border-b border-stone-200">
                <h3 className="font-semibold text-stone-900 flex items-center">
                  <Shield className="w-4 h-4 mr-2 text-indigo-600" />
                  Protection Deposits
                </h3>
              </div>
              
              <div className="divide-y divide-stone-100">
                {balance.protection_status.map((protection) => (
                  <motion.div
                    key={protection.club_id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="px-6 py-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <span className="font-medium text-stone-900">
                          {protection.club_name}
                        </span>
                        <Badge 
                          className={`ml-2 ${getStatusColor(protection.status)}`}
                          variant="outline"
                        >
                          {getStatusIcon(protection.status)}
                          <span className="ml-1">{protection.percentage}%</span>
                        </Badge>
                      </div>
                      
                      {protection.needs_reload && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openReloadDialog(protection.club_id, protection.club_name)}
                          className="text-amber-600 border-amber-300 hover:bg-amber-50"
                          data-testid={`reload-btn-${protection.club_id}`}
                        >
                          <RefreshCw className="w-3 h-3 mr-1" />
                          Reload
                        </Button>
                      )}
                    </div>

                    <div className="flex items-center text-sm text-stone-500 mb-2">
                      <span>${protection.current_deposit.toFixed(2)}</span>
                      <ArrowRight className="w-3 h-3 mx-2" />
                      <span>${protection.required_deposit.toFixed(2)} required</span>
                    </div>

                    <Progress 
                      value={protection.percentage} 
                      className={`h-2 ${
                        protection.status === 'critical' ? 'bg-red-100' :
                        protection.status === 'warning' ? 'bg-amber-100' :
                        'bg-green-100'
                      }`}
                    />
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Low Protection Warning */}
          {balance?.has_low_protection && (
            <div className="px-6 py-4 bg-amber-50 border-t border-amber-200">
              <div className="flex items-center text-amber-800">
                <AlertCircle className="w-5 h-5 mr-2" />
                <span className="text-sm font-medium">
                  Some protection deposits are low. Reload to enable contributions and payouts.
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reload Dialog */}
      <Dialog open={reloadDialog.open} onOpenChange={(open) => setReloadDialog({ ...reloadDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <RefreshCw className="w-5 h-5 mr-2 text-indigo-600" />
              Reload Protection Deposit
            </DialogTitle>
            <DialogDescription>
              Add funds to your protection deposit for {reloadDialog.clubName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-4 bg-stone-50 rounded-lg">
              <div className="text-sm text-stone-500 mb-1">Available Balance</div>
              <div className="text-2xl font-bold text-green-600">
                ${balance?.available_balance?.toFixed(2) || '0.00'}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reload-amount">Amount to Add</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="reload-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={balance?.available_balance || 0}
                  value={reloadAmount}
                  onChange={(e) => setReloadAmount(e.target.value)}
                  placeholder="0.00"
                  className="pl-9"
                  data-testid="reload-amount-input"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReloadDialog({ open: false, clubId: null, clubName: '' })}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReloadProtection}
              disabled={processing || !reloadAmount}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="confirm-reload-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reload ${reloadAmount || '0.00'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
